"use client";

import { useEffect, useId, useRef, useState } from "react";
import Link from "next/link";
import BaseModal from "./BaseModal";
import { useAuth } from "../AuthProvider";
import { useModal } from "./ModalProvider";
import { buildOAuthCallbackUrl, logOAuthStart } from "@/lib/auth/oauthRedirect";
import {
  beginExplicitLoginIntent,
  clearExplicitLoginIntent,
} from "@/lib/auth/explicitLoginIntent";
import {
  authErrorClassName,
  authGoogleButtonClassName,
  authInputClassName,
  authLabelClassName,
  authPrimaryButtonClassName,
  authSwitchLinkClassName,
} from "@/components/auth/authFormStyles";
import PasswordRequirements from "@/components/auth/PasswordRequirements";
import {
  getPasswordErrorMessage,
  getPasswordSubmissionErrorMessage,
  getPasswordValidationErrorCode,
  PASSWORD_ERROR_CODES,
  PASSWORD_MIN_LENGTH,
} from "@/lib/auth/passwordErrors";

const INVITE_EMAIL_MISMATCH_MESSAGE = "Use the invited email to create an account for this staff invite.";
// Messages that describe a problem with the password itself (client rules or Supabase weak-password responses).
const PASSWORD_FIELD_ERROR_MESSAGES = new Set(
  Object.values(PASSWORD_ERROR_CODES)
    .filter((code) => code !== PASSWORD_ERROR_CODES.generic)
    .map((code) => getPasswordErrorMessage(code))
);

function resolveSignupDestination(next) {
  if (typeof next !== "string") return "/customer/home";
  const trimmed = next.trim();
  if (!trimmed || !trimmed.startsWith("/") || trimmed.startsWith("//")) {
    return "/customer/home";
  }
  if (trimmed.startsWith("/api/") || trimmed.startsWith("/_next/")) {
    return "/customer/home";
  }
  return trimmed;
}

export default function CustomerSignupModal({
  onClose,
  next = null,
  initialEmail = "",
  lockEmail = false,
  context = null,
  initialStep = "account",
}) {
  const { supabase } = useAuth();
  const { openModal } = useModal();

  const [email, setEmail] = useState(() => String(initialEmail || "").trim().toLowerCase());
  const [password, setPassword] = useState("");
  const [fullName, setFullName] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [legalAccepted, setLegalAccepted] = useState(false);
  const [marketingEmailConsent, setMarketingEmailConsent] = useState(false);
  const [legalAttempted, setLegalAttempted] = useState(false);
  const [legalError, setLegalError] = useState("");
  const errorId = useId();
  const legalErrorId = useId();
  const [step, setStep] = useState(
    initialStep === "account-setup" || initialStep === "email-preferences"
      ? "account-setup"
      : "account"
  );
  const [pendingDestination, setPendingDestination] = useState(null);
  const submitLockRef = useRef(false);

  const friendlyExistingAccountMessage =
    "An account with this email already exists. Log in instead.";
  const legalAcceptanceError =
    "Please accept the Terms of Service and Privacy Policy to continue.";
  const destination = resolveSignupDestination(next);
  const isStaffInviteContext = context === "staff-invite";
  const shouldLockEmail = isStaffInviteContext && lockEmail && Boolean(initialEmail);
  const signupBlocked = !legalAccepted || loading;
  const blockedButtonClassName =
    "cursor-not-allowed opacity-70 hover:bg-inherit";
  const blockedGoogleButtonClassName =
    "cursor-not-allowed border-slate-300 bg-slate-50 text-slate-600 hover:bg-slate-50";

  function isAlreadyRegisteredError(authError) {
    const message = String(authError?.message || "").toLowerCase();
    const code = String(authError?.code || authError?.name || "").toLowerCase();
    return (
      message.includes("user already registered") ||
      message.includes("already registered") ||
      code.includes("user_already_exists") ||
      code.includes("email_exists")
    );
  }

  useEffect(() => {
    const normalized = String(initialEmail || "").trim().toLowerCase();
    if (normalized) setEmail(normalized);
  }, [initialEmail]);

  async function persistSession(session) {
    const debugAuth = process.env.NEXT_PUBLIC_DEBUG_AUTH === "1";

    if (!session?.access_token || !session?.refresh_token) {
      const { data } = await supabase.auth.getSession();
      session = data?.session;
    }

    if (!session?.access_token || !session?.refresh_token) {
      throw new Error("missing_session");
    }

    if (debugAuth) {
      console.log("[customer-signup] refreshing cookies with tokens");
    }

    const res = await fetch("/api/auth/refresh", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        access_token: session.access_token,
        refresh_token: session.refresh_token,
      }),
    });

    const refreshed = res.headers.get("x-auth-refresh-user") === "1";
    if (debugAuth) {
      console.log(
        "[customer-signup] refresh user header",
        res.headers.get("x-auth-refresh-user")
      );
    }

    if (!refreshed) {
      throw new Error("refresh_failed");
    }

    return session;
  }

  async function fetchFreshProfile() {
    const res = await fetch("/api/me", {
      method: "GET",
      credentials: "include",
      cache: "no-store",
      headers: {
        "x-yb-auth-bootstrap": "customer_signup",
      },
    });
    const payload = await res.json().catch(() => ({}));
    if (!res.ok || !payload?.user?.id || !payload?.profile?.id) {
      throw new Error(payload?.error || "profile_missing");
    }
    return payload;
  }

  async function recordLegalAcceptance() {
    const res = await fetch("/api/account/legal-acceptances", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        customer_terms_accepted: true,
        source: "signup",
      }),
    });
    const payload = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw new Error(payload?.error || "legal_acceptance_failed");
    }
    return payload;
  }

  async function savePostSignupSetup({ name, marketingConsent }) {
    try {
      const trimmedName = String(name || "").trim();
      const body = {
        marketing_email_consent: marketingConsent === true,
        ...(trimmedName ? { full_name: trimmedName } : {}),
      };
      const res = await fetch("/api/account/profile", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const payload = await res.json().catch(() => ({}));
        throw new Error(payload?.error || "post_signup_setup_failed");
      }
    } catch (setupError) {
      console.warn("Post-signup setup preferences were not saved", setupError);
    }
  }

  async function createOAuthLegalAcceptanceMarker() {
    const res = await fetch("/api/auth/oauth-legal-acceptance", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        customer_terms_accepted: true,
        role: "customer",
        source: "customer_google_signup",
      }),
    });
    const payload = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw new Error(payload?.error || "oauth_legal_acceptance_marker_failed");
    }
    return payload;
  }

  async function recoverExistingSessionForEmail() {
    const { data } = await supabase.auth.getSession();
    const session = data?.session;
    const sessionEmail = String(session?.user?.email || "").trim().toLowerCase();
    const requestedEmail = String(email || "").trim().toLowerCase();

    if (!session?.user?.id || !requestedEmail || sessionEmail !== requestedEmail) {
      return false;
    }

    await persistSession(session);
    await fetchFreshProfile();
    onClose?.();
    window.location.replace(destination);
    return true;
  }

  async function handleSignup(event) {
    event.preventDefault();
    if (submitLockRef.current) return;

    submitLockRef.current = true;
    setError("");
    setLegalAttempted(true);
    setLegalError("");

    if (!legalAccepted) {
      setLegalError(legalAcceptanceError);
      submitLockRef.current = false;
      return;
    }

    setLoading(true);

    try {
      const normalizedEmail = String(email || "").trim().toLowerCase();
      const normalizedInitialEmail = String(initialEmail || "").trim().toLowerCase();
      if (shouldLockEmail && normalizedEmail !== normalizedInitialEmail) {
        setError(INVITE_EMAIL_MISMATCH_MESSAGE);
        return;
      }

      const validationError = getPasswordValidationErrorCode(password);
      if (validationError) {
        setError(getPasswordErrorMessage(validationError));
        return;
      }

      const { data: signUpData, error: signUpError } = await supabase.auth.signUp({
        email: normalizedEmail || email,
        password,
        options: {
          data: {
            role: "customer",
          },
        },
      });

      if (signUpError) {
        if (isAlreadyRegisteredError(signUpError)) {
          const recovered = await recoverExistingSessionForEmail();
          if (!recovered) {
            setError(friendlyExistingAccountMessage);
          }
        } else {
          setError(
            getPasswordSubmissionErrorMessage(
              signUpError,
              "Unable to create your account. Please try again."
            )
          );
        }
        return;
      }

      const authUser = signUpData?.user;
      if (!authUser) {
        setError("Signup succeeded but no user returned. Try logging in.");
        return;
      }
      if (Array.isArray(authUser.identities) && authUser.identities.length === 0) {
        setError(friendlyExistingAccountMessage);
        return;
      }

      await persistSession(signUpData?.session);
      await fetchFreshProfile();
      if (!isStaffInviteContext) {
        await recordLegalAcceptance();
      }

      if (isStaffInviteContext) {
        onClose?.();
        window.location.replace(destination);
        return;
      }

      setPendingDestination(destination);
      setStep("account-setup");
    } catch (err) {
      console.error("Customer signup completion failed", err);
      if (err?.message === "missing_session" || err?.message === "refresh_failed") {
        setError(
          "Signup succeeded but session could not be persisted in Safari. Please try again."
        );
        return;
      }
      setError("Account created, but failed to finish profile. Try logging in.");
    } finally {
      submitLockRef.current = false;
      setLoading(false);
    }
  }

  async function handleGoogleSignup(event) {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    setError("");
    setLegalAttempted(true);
    setLegalError("");

    if (!legalAccepted) {
      setLegalError(legalAcceptanceError);
      return;
    }

    setLoading(true);

    try {
      await createOAuthLegalAcceptanceMarker();
    } catch (markerError) {
      console.error("Customer Google signup legal marker failed", markerError);
      setLegalError("Could not start Google signup. Please try again.");
      setLoading(false);
      return;
    }

    const currentOrigin =
      typeof window !== "undefined" ? window.location.origin : "";
    const explicitLoginIntent = beginExplicitLoginIntent("oauth");
    const redirectTo = buildOAuthCallbackUrl({
      currentOrigin,
      next: destination,
      intent: "customer",
      loginIntentId: explicitLoginIntent?.id,
    });
    logOAuthStart({ provider: "google", redirectTo, currentOrigin });

    const { error: oauthError } = await supabase.auth.signInWithOAuth({
      provider: "google",
      // Use shared OAuth callback to exchange code + create profile
      options: { redirectTo },
    });

    if (oauthError) {
      clearExplicitLoginIntent();
      setError(oauthError.message);
      setLoading(false);
      return;
    }

    onClose?.();
  }

  async function completeSetup({ skipped = false } = {}) {
    setLoading(true);
    setError("");
    try {
      await savePostSignupSetup({
        name: skipped ? "" : fullName,
        marketingConsent: skipped ? false : marketingEmailConsent,
      });
    } finally {
      setLoading(false);
      onClose?.();
      if (pendingDestination) {
        window.location.replace(pendingDestination);
      }
    }
  }

  if (step === "account-setup") {
    return (
      <BaseModal
        title="Finish setting up"
        description="Add your name and choose whether to get local updates."
        onClose={() => {
          if (!loading) {
            completeSetup({ skipped: true });
          }
        }}
        compactScrollable
      >
        <form
          className="pt-1.5 pb-1"
          onSubmit={(event) => {
            event.preventDefault();
            completeSetup();
          }}
        >
          <div>
            <label htmlFor="customer-post-signup-full-name" className={authLabelClassName}>
              Full name
            </label>
            <input
              id="customer-post-signup-full-name"
              type="text"
              autoComplete="name"
              value={fullName}
              onChange={(event) => setFullName(event.target.value)}
              className={`${authInputClassName} mt-2`}
              disabled={loading}
            />
            <p className="mt-1.5 text-xs leading-5 text-slate-500">
              Optional
            </p>
          </div>

          <div className="mt-5">
            <label className="flex cursor-pointer items-start gap-3 text-sm leading-5 text-slate-700">
              <input
                type="checkbox"
                checked={marketingEmailConsent}
                onChange={(event) => setMarketingEmailConsent(event.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-slate-300 text-purple-600 focus:ring-2 focus:ring-purple-500/40"
                disabled={loading}
              />
              <span>Send me local offers and YourBarrio updates</span>
            </label>
            <p className="mt-1 pl-7 text-xs leading-4 text-slate-500">
              You can unsubscribe anytime.
            </p>
          </div>

          {error ? (
            <div id={errorId} role="alert" className={authErrorClassName}>
              {error}
            </div>
          ) : null}

          <button
            type="submit"
            disabled={loading}
            className={`${authPrimaryButtonClassName} mt-5`}
          >
            {loading ? "Saving..." : "Save and continue"}
          </button>

          <button
            type="button"
            disabled={loading}
            onClick={() => completeSetup({ skipped: true })}
            className="mt-3 w-full text-sm font-semibold text-slate-600 underline-offset-2 hover:text-slate-900 hover:underline disabled:opacity-60"
          >
            Skip for now
          </button>
        </form>
      </BaseModal>
    );
  }

  // Mark fields invalid only when the visible error is about that field; server/account errors stay form-level.
  const emailInvalid = error === INVITE_EMAIL_MISMATCH_MESSAGE;
  const passwordInvalid = PASSWORD_FIELD_ERROR_MESSAGES.has(error);
  const legalInvalid = legalAttempted && legalError === legalAcceptanceError;

  return (
    <BaseModal
      title={isStaffInviteContext ? "Create account to accept invite" : "Create your customer account"}
      description={
        isStaffInviteContext
          ? "Create one YourBarrio account with the invited email. Staff access is added after you accept the invite."
          : "Join YourBarrio to save your favorite local businesses and see recommendations near you."
      }
      onClose={onClose}
      compactScrollable
    >
      <form onSubmit={handleSignup} className="space-y-4">
        <div className="space-y-2">
          <label htmlFor="customer-signup-email" className={authLabelClassName}>Email</label>
          <input
            id="customer-signup-email"
            name="email"
            type="email"
            required
            autoComplete="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            aria-invalid={emailInvalid || undefined}
            aria-describedby={emailInvalid ? errorId : undefined}
            className={authInputClassName}
            disabled={loading || shouldLockEmail}
            readOnly={shouldLockEmail}
          />
          {shouldLockEmail ? (
            <p className="text-xs leading-5 text-slate-500">
              This invite can only be accepted with {String(initialEmail || "").trim().toLowerCase()}.
            </p>
          ) : null}
        </div>

        <div className="space-y-2">
          <label htmlFor="customer-signup-password" className={authLabelClassName}>Password</label>
          <input
            id="customer-signup-password"
            name="password"
            type="password"
            required
            minLength={PASSWORD_MIN_LENGTH}
            autoComplete="new-password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            aria-invalid={passwordInvalid || undefined}
            aria-describedby={passwordInvalid ? errorId : undefined}
            className={authInputClassName}
            disabled={loading}
          />
          <PasswordRequirements />
        </div>

        {error ? (
          <div id={errorId} role="alert" className={authErrorClassName}>
            {error}
          </div>
        ) : null}

        <div className="mt-3 space-y-2">
          <label className="flex cursor-pointer items-start gap-2 text-xs leading-5 text-slate-600">
            <input
              type="checkbox"
              checked={legalAccepted}
              onChange={(event) => {
                setLegalAccepted(event.target.checked);
                if (event.target.checked) setLegalError("");
              }}
              aria-invalid={legalInvalid || undefined}
              aria-describedby={legalInvalid ? legalErrorId : undefined}
              className="mt-0.5 h-4 w-4 rounded border-slate-300 text-purple-600 focus:ring-2 focus:ring-purple-500/40"
              disabled={loading}
            />
            <span>
              By creating an account, you agree to the YourBarrio{" "}
              <Link
                href="/terms"
                target="_blank"
                rel="noopener noreferrer"
                className="font-semibold text-purple-700 underline-offset-2 hover:underline"
              >
                Terms of Service
              </Link>{" "}
              and acknowledge the{" "}
              <Link
                href="/privacy"
                target="_blank"
                rel="noopener noreferrer"
                className="font-semibold text-purple-700 underline-offset-2 hover:underline"
              >
                Privacy Policy
              </Link>
              .
            </span>
          </label>
          {legalAttempted && legalError ? (
            <p id={legalErrorId} role="alert" className="text-xs leading-5 text-rose-600/90">{legalError}</p>
          ) : null}
        </div>

        <button
          type="submit"
          disabled={loading}
          aria-disabled={signupBlocked}
          className={`${authPrimaryButtonClassName} mt-2 ${!legalAccepted ? blockedButtonClassName : ""}`}
        >
          {loading ? "Creating account..." : "Create account"}
        </button>
      </form>

      {!isStaffInviteContext ? (
        <>
          <div className="my-4 flex items-center gap-3 text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">
            <div className="h-px flex-1 bg-slate-200" />
            <span>or</span>
            <div className="h-px flex-1 bg-slate-200" />
          </div>

          <div>
            <button
              type="button"
              onClick={handleGoogleSignup}
              disabled={loading}
              aria-disabled={signupBlocked}
              className={`${authGoogleButtonClassName} ${!legalAccepted ? blockedGoogleButtonClassName : ""}`}
            >
              <img src="/google-icon.svg" className="h-5 w-5" alt="Google" />
              Continue with Google
            </button>
          </div>
        </>
      ) : null}

      <p className="mt-4 text-center text-sm text-slate-700">
        Already have an account?{" "}
        <button
          type="button"
          onClick={() =>
            openModal("customer-login", {
              ...(next ? { next } : {}),
              ...(initialEmail ? { initialEmail } : {}),
              ...(context ? { context } : {}),
              ...(lockEmail ? { lockEmail } : {}),
            })
          }
          className={authSwitchLinkClassName}
        >
          Log in
        </button>
      </p>
    </BaseModal>
  );
}
